import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import PhoneInput from './PhoneInput';
import { getCountry, isValidPhone, formatPhone } from '../data/countryCodes';

const services = [
  'Custom Software Development', 'Web Application Development', 'Mobile App Development',
  'Software Integration', 'Website Design & Development', 'E-Commerce Solutions',
  'AI & Machine Learning', 'Chatbots & Virtual Assistants', 'Business Intelligence',
  'Data Analytics & Reporting', 'Database Design & Management',
  'Web Hosting & Domain Management', 'Other / Multiple Services',
];

// Phone is optional; its country code defaults to India.
const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '', phoneCountry: 'IN',
  company: '', service: '', message: '',
};

// Where enquiries go. Netlify builds set REACT_APP_CONTACT_BACKEND=netlify
// (netlify.toml) and use Netlify Forms, which emails them on; any other build
// (the GoDaddy upload) posts to public/contact.php, which needs PHP.
const USE_NETLIFY_FORMS = process.env.REACT_APP_CONTACT_BACKEND === 'netlify';

// Encodes a plain object as an application/x-www-form-urlencoded body.
const encodeFormData = (data) =>
  Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');

export default function Contact() {
  const { colors, theme } = useTheme();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitHover, setSubmitHover] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = true;
    if (!form.lastName.trim()) e.lastName = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (form.phone.trim() && !isValidPhone(form.phone, getCountry(form.phoneCountry).dial)) e.phone = true;
    if (!form.message.trim()) e.message = true;
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSending(true);
    setSubmitFailed(false);

    const fields = {
      'bot-field': '',
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      // e.g. "+91 98765 43210 (India)"; blank when not given
      phone: form.phone.trim() ? formatPhone(form.phone, form.phoneCountry) : '',
      company: form.company,
      service: form.service,
      message: form.message,
    };

    try {
      if (USE_NETLIFY_FORMS) {
        // Netlify Forms: post to the static file that registers the form
        // (public/__forms.html). Any 2xx means it was stored.
        const response = await fetch('/__forms.html', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: encodeFormData({ 'form-name': 'contact', ...fields }),
        });
        if (!response.ok) throw new Error(`Form submission failed: ${response.status}`);
      } else {
        // public/contact.php (runs on the GoDaddy hosting) validates this and
        // emails it to info@deckfortech.com. It replies {"ok": true} on success.
        const response = await fetch('/contact.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: encodeFormData(fields),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) throw new Error(`Form submission failed: ${response.status}`);
      }

      setSubmitted(true);
      setForm(EMPTY_FORM);
      setErrors({});
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setSubmitFailed(true);
    } finally {
      setSending(false);
    }
  };

  const update = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors(er => ({ ...er, [field]: false }));
  };

  const s = {
    section: { padding: '100px 5vw', background: colors.bg2 },
    // Single centred column: email/call live in the navbar's "Get in touch"
    inner: { maxWidth: '760px', margin: '0 auto' },
    title: {
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: 'clamp(2rem, 4vw, 3.2rem)',
      lineHeight: 1, letterSpacing: '2px', color: colors.heading, marginBottom: '2rem',
      textAlign: 'center',
    },
    form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    group: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    label: {
      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '1px',
      textTransform: 'uppercase', color: colors.muted,
    },
    input: {
      background: colors.surface, border: `1px solid ${colors.border}`,
      color: colors.text, padding: '0.9rem 1rem',
      fontSize: '0.9rem', outline: 'none',
      transition: 'border-color 0.2s', width: '100%',
      fontFamily: "'Inter', sans-serif",
    },
    // Dropdown options can't use the input's gradient background, so give them
    // a solid colour per theme (otherwise Windows shows light text on white).
    option: {
      background: theme === 'dark' ? '#161c2e' : '#ffffff',
      color: colors.text,
    },
    submit: {
      padding: '1rem 2rem', fontSize: '0.9rem', fontWeight: 700,
      letterSpacing: '2px', textTransform: 'uppercase',
      border: `2px solid ${colors.text}`, cursor: 'pointer',
      transition: 'all 0.2s', marginTop: '0.5rem',
    },
    success: {
      background: 'rgba(76,175,80,0.08)', border: '1px solid #4caf50',
      padding: '1rem', color: '#4caf50', fontSize: '0.88rem', textAlign: 'center',
    },
    error: {
      background: 'rgba(255,61,107,0.08)', border: '1px solid #ff3d6b',
      padding: '1rem', color: '#ff3d6b', fontSize: '0.88rem', textAlign: 'center',
    },
  };

  const inputStyle = (field) => ({
    ...s.input,
    borderColor: errors[field] ? '#ff3d6b' : colors.border,
  });

  return (
    <section id="contact" style={s.section} className="contact-grid">
      <div style={s.inner}>
        <h2 style={s.title}>SEND US A MESSAGE</h2>

        <div style={s.form}>
          <div style={s.row} className="form-row">
            <div style={s.group}>
              <label style={s.label}>First Name *</label>
              <input style={inputStyle('firstName')} placeholder="First Name" value={form.firstName} onChange={update('firstName')} />
            </div>
            <div style={s.group}>
              <label style={s.label}>Last Name *</label>
              <input style={inputStyle('lastName')} placeholder="Last Name" value={form.lastName} onChange={update('lastName')} />
            </div>
          </div>
          <div style={s.group}>
            <label style={s.label}>Email Address *</label>
            <input style={inputStyle('email')} type="email" placeholder="you@company.com" value={form.email} onChange={update('email')} />
          </div>
          <div style={s.group}>
            <label style={s.label}>Phone Number</label>
            <PhoneInput
              countryIso={form.phoneCountry}
              onCountryChange={(iso) => {
                setForm(f => ({ ...f, phoneCountry: iso }));
                if (errors.phone) setErrors(er => ({ ...er, phone: false }));
              }}
              value={form.phone}
              onChange={(v) => {
                setForm(f => ({ ...f, phone: v }));
                if (errors.phone) setErrors(er => ({ ...er, phone: false }));
              }}
              invalid={errors.phone}
              inputStyle={s.input}
              codeWidth="112px"
            />
            {errors.phone && (
              <span style={{ fontSize: '0.78rem', color: '#ff3d6b' }}>
                Please enter a valid phone number for the selected country code.
              </span>
            )}
          </div>
          <div style={s.group}>
            <label style={s.label}>Company</label>
            <input style={s.input} placeholder="Your Company" value={form.company} onChange={update('company')} />
          </div>
          <div style={s.group}>
            <label style={s.label}>Service Interested In</label>
            <select style={s.input} value={form.service} onChange={update('service')}>
              <option value="" style={s.option}>Select a service...</option>
              {services.map(svc => <option key={svc} value={svc} style={s.option}>{svc}</option>)}
            </select>
          </div>
          <div style={s.group}>
            <label style={s.label}>Project Details *</label>
            <textarea
              style={{ ...inputStyle('message'), resize: 'none' }}
              rows={5}
              placeholder="Tell us about your project, goals, and timeline..."
              value={form.message}
              onChange={update('message')}
            />
          </div>
          <button
            style={{
              ...s.submit,
              background: submitHover ? 'transparent' : colors.accentBg,
              color: submitHover ? colors.accentBg : colors.accentText,
              opacity: sending ? 0.6 : 1,
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={() => setSubmitHover(true)}
            onMouseLeave={() => setSubmitHover(false)}
            onClick={handleSubmit}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send Message →'}
          </button>
          {submitted && (
            <div style={s.success}>
              Thank you for contacting Deck For Tech. Your message has been received, and a member of our team will be in touch with you shortly.
            </div>
          )}
          {submitFailed && (
            <div style={s.error}>
              Something went wrong sending your message. Please try again, or use Get in Touch at the top of the page.
            </div>
          )}
          {Object.keys(errors).length > 0 && (
            <div style={s.error}>Please fill in all required fields correctly.</div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 600px) {
          .form-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .contact-grid h2 { white-space: normal !important; }
        }
      `}</style>
    </section>
  );
}
